import { useEffect } from 'react';

/**
 * Per-route document head.
 *
 * No dependency for this: the app needs a title, a description, a canonical
 * and a handful of social tags, and react-helmet is 12KB to do what forty
 * lines of DOM manipulation does. The tags are also written into the
 * prerendered HTML at build time (see scripts/prerender.mjs), so a crawler
 * that never runs JavaScript still sees them — this hook only keeps them
 * correct as a visitor navigates.
 */

export const SITE_NAME = 'Merritt Island RV Parking';
export const SITE_URL = 'https://rv.parktle.com';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export function useSeo({ title, description, path, image, noindex = false }) {
  useEffect(() => {
    // Titles read "page — site" so a tab or a result line is identifiable when
    // truncated. Skipped when the title already carries the brand: this site is
    // named after its only facility, so appending would stutter the name twice.
    const fullTitle = title && !title.includes(SITE_NAME) ? `${title} — ${SITE_NAME}` : (title || SITE_NAME);
    document.title = fullTitle;

    const url = `${SITE_URL}${path ?? window.location.pathname}`;
    const img = image ?? `${SITE_URL}/social-card.png`;

    if (description) upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertLink('canonical', url);

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: img });
    if (description) upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: img });

    // Signed-in screens must never be indexed. They are behind auth anyway, but
    // a crawler that follows a shared link should be told plainly.
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow',
    });
  }, [title, description, path, image, noindex]);
}

/**
 * Structured data.
 *
 * Keyed by id so a route swap replaces its own block rather than stacking a
 * second one — two LocalBusiness blocks on a page is worse than none, because
 * Google picks one and you cannot tell which.
 */
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = `ld-${id}`;
    el.textContent = JSON.stringify(data);
    document.head.querySelector(`#ld-${id}`)?.remove();
    document.head.appendChild(el);
    return () => document.head.querySelector(`#ld-${id}`)?.remove();
  }, [id, data]);
}

/**
 * Schema.org for a storage yard.
 *
 * SelfStorage is the specific type Google understands for this business, and
 * the address, geo and opening hours are what surface in a local pack result.
 */
export function selfStorageSchema(facility) {
  if (!facility) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: facility.name,
    description: facility.description || undefined,
    url: `${SITE_URL}/facilities/${facility.slug}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: facility.address_line1,
      addressLocality: facility.city,
      addressRegion: facility.state_code,
      postalCode: facility.postal_code,
      addressCountry: facility.country_code ?? 'US',
    },
    geo: { '@type': 'GeoCoordinates', latitude: facility.lat, longitude: facility.lng },
    telephone: facility.phone || undefined,
    email: facility.email || undefined,
    openingHours: facility.is_24h_access
      ? 'Mo-Su 00:00-23:59'
      : (facility.gate_opens_at && facility.gate_closes_at
          ? `Mo-Su ${String(facility.gate_opens_at).slice(0, 5)}-${String(facility.gate_closes_at).slice(0, 5)}`
          : undefined),
    priceRange: facility.min_rate_cents
      ? `$${Math.round(facility.min_rate_cents / 100)}-$${Math.round((facility.max_rate_cents ?? facility.min_rate_cents) / 100)}`
      : undefined,
    amenityFeature: [
      facility.has_gate && 'Gated entry',
      facility.has_cameras && 'Camera coverage',
      facility.has_perimeter_fence && 'Perimeter fence',
      facility.has_dump_station && 'Dump station',
      facility.has_wash_bay && 'Wash bay',
      facility.has_potable_water && 'Potable water',
    ].filter(Boolean).map((name) => ({ '@type': 'LocationFeatureSpecification', name, value: true })),
  };
}
